import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Search,
  Plus,
  Ticket,
  MessageSquare,
  Clock,
  AlertCircle,
  CheckCircle2,
  User,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Building2,
  Handshake,
  X,
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/contexts/AuthContext';
import {
  GET_CASES,
  GET_CASE_STATS,
  CREATE_CASE,
  UPDATE_CASE,
  type Case,
  type CasePriority,
  type CaseStatus,
  type CaseType,
  type GetCasesData,
  type GetCasesVariables,
  type GetCaseStatsData,
  type CreateCaseData,
  type UpdateCaseData,
} from '@/graphql/cases';
import {
  GET_ORGANIZATIONS,
  type GetOrganizationsData,
} from '@/graphql/organizations';

const PRIORITY_BADGES: Record<CasePriority, { label: string; className: string; icon: typeof AlertCircle }> = {
  low: { label: 'Low', className: 'badge-neutral', icon: Clock },
  medium: { label: 'Medium', className: 'badge-primary', icon: Clock },
  high: { label: 'High', className: 'badge-warning', icon: AlertCircle },
  urgent: { label: 'Urgent', className: 'bg-rust/10 text-rust dark:bg-salmon/20 dark:text-salmon', icon: AlertCircle },
};

const STATUS_BADGES: Record<CaseStatus, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light' },
  open: { label: 'Open', className: 'badge-primary' },
  pending: { label: 'Pending', className: 'badge-warning' },
  resolved: { label: 'Resolved', className: 'badge-secondary' },
  closed: { label: 'Closed', className: 'badge-neutral' },
};

const ITEMS_PER_PAGE = 20;

export function CaseList() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<CaseStatus | 'all' | 'active'>('active');
  const [priorityFilter, setPriorityFilter] = useState<CasePriority | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Create form state
  const [formSubject, setFormSubject] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formOrganizationId, setFormOrganizationId] = useState('');
  const [formCaseType, setFormCaseType] = useState<CaseType>('software');
  const [formPriority, setFormPriority] = useState<CasePriority>('medium');
  const [isSaving, setIsSaving] = useState(false);
  
  // Client search state
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [debouncedClientSearch, setDebouncedClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientSearchRef = useRef<HTMLDivElement>(null);

  // Debounce client search for server-side searching
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClientSearch(clientSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearchQuery]);

  // Query cases
  const { data, loading, error, refetch } = useQuery<GetCasesData, GetCasesVariables>(
    GET_CASES,
    {
      variables: {
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  // Query stats
  const { data: statsData } = useQuery<GetCaseStatsData>(GET_CASE_STATS);

  // Query organizations for create modal with server-side search
  const { data: orgsData, loading: orgsLoading } = useQuery<GetOrganizationsData>(GET_ORGANIZATIONS, {
    variables: { 
      search: debouncedClientSearch || undefined,
      limit: 50,
    },
    skip: !showCreateModal, // Only query when modal is open
  });

  // Mutations
  const [createCase] = useMutation<CreateCaseData>(CREATE_CASE);
  const [updateCase] = useMutation<UpdateCaseData>(UPDATE_CASE);

  const cases = data?.cases.items || [];
  const pageInfo = data?.cases.pageInfo;
  const totalPages = pageInfo?.totalPages || 1;
  const stats = statsData?.caseStats;
  const organizations = orgsData?.organizations.items || [];

  // Organizations from server-side search (no client-side filtering needed)
  const filteredOrganizations = organizations;

  // Get selected organization name
  const selectedOrganization = organizations.find(org => org.id === formOrganizationId);

  // Close client dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: CaseStatus | 'all' | 'active') => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePriorityChange = (value: CasePriority | 'all') => {
    setPriorityFilter(value);
    setCurrentPage(1);
  };

  const resetForm = () => {
    setFormSubject('');
    setFormDescription('');
    setFormOrganizationId('');
    setFormCaseType('software');
    setFormPriority('medium');
    setClientSearchQuery('');
    setShowClientDropdown(false);
  };

  const handleSelectClient = (orgId: string, orgName: string) => {
    setFormOrganizationId(orgId);
    setClientSearchQuery(orgName);
    setShowClientDropdown(false);
  };

  const handleClearClient = () => {
    setFormOrganizationId('');
    setClientSearchQuery('');
  };

  const handleCreateCase = async () => {
    if (!formSubject || !formOrganizationId) return;

    setIsSaving(true);
    try {
      await createCase({
        variables: {
          input: {
            subject: formSubject,
            description: formDescription || undefined,
            organizationId: formOrganizationId,
            caseType: formCaseType,
            priority: formPriority,
          },
        },
      });
      setShowCreateModal(false);
      resetForm();
      refetch();
    } catch (err) {
      console.error('Create case failed:', err);
      alert('Failed to create case. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickResolve = async (caseItem: Case, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateCase({
        variables: {
          id: caseItem.id,
          input: { status: 'resolved' },
        },
      });
      refetch();
    } catch (err) {
      console.error('Resolve failed:', err);
    }
  };

  if (error) {
    return (
      <div className="p-8 text-center text-rust">
        <AlertCircle className="w-12 h-12 mx-auto mb-3" />
        <p>Error loading cases: {error.message}</p>
        <button onClick={() => refetch()} className="btn-secondary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate dark:text-mist">
            Service Cases
          </h1>
          <p className="text-chalkboard dark:text-rain-cloud mt-1">
            {stats?.active || 0} active case{stats?.active !== 1 ? 's' : ''}
            {(stats?.urgent || 0) > 0 && (
              <span className="text-rust dark:text-salmon"> • {stats?.urgent} urgent</span>
            )}
          </p>
        </div>
        {canWrite('cases') && (
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            New Case
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-chalkboard dark:text-rain-cloud">Total Cases</p>
            <p className="text-2xl font-semibold text-slate dark:text-mist">{stats?.total || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-chalkboard dark:text-rain-cloud">Active</p>
            <p className="text-2xl font-semibold text-pond dark:text-pond-light">{stats?.active || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-chalkboard dark:text-rain-cloud">Urgent</p>
            <p className="text-2xl font-semibold text-rust dark:text-salmon">{stats?.urgent || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-chalkboard dark:text-rain-cloud">Unassigned</p>
            <p className="text-2xl font-semibold text-orange">{stats?.unassigned || 0}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
            <div className="relative" style={{ flex: '1 1 auto', minWidth: 0 }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-french-gray pointer-events-none" />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="input"
                style={{ width: '100%', paddingLeft: '2.5rem' }}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value as CaseStatus | 'all' | 'active')}
              className="select"
              style={{ flex: '0 0 auto', width: '10rem' }}
            >
              <option value="active">Active Cases</option>
              <option value="all">All Cases</option>
              <option value="new">New</option>
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => handlePriorityChange(e.target.value as CasePriority | 'all')}
              className="select"
              style={{ flex: '0 0 auto', width: '10rem' }}
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cases Table */}
      <div className="card overflow-hidden">
        {loading && cases.length === 0 ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-pond" />
            <p className="text-chalkboard dark:text-rain-cloud">Loading cases...</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Case</th>
                <th>Client</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Updated</th>
                <th className="w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((caseItem) => {
                const priorityBadge = PRIORITY_BADGES[caseItem.priority];
                const statusBadge = STATUS_BADGES[caseItem.status];
                const PriorityIcon = priorityBadge.icon;

                return (
                  <tr
                    key={caseItem.id}
                    onClick={() => navigate(`/cases/${caseItem.id}`)}
                    className="cursor-pointer"
                  >
                    <td>
                      <div className="flex items-start gap-2">
                        <Ticket className="w-4 h-4 text-chalkboard dark:text-rain-cloud mt-0.5" />
                        <div>
                          <span className="font-medium text-pond dark:text-pond-light block">
                            {caseItem.ticketNumber}
                          </span>
                          <span className="text-sm text-slate dark:text-mist">
                            {caseItem.subject}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <p className="text-sm text-slate dark:text-mist">
                          {caseItem.organization?.name || 'Unknown'}
                        </p>
                        {caseItem.contact && (
                          <p className="text-xs text-chalkboard dark:text-rain-cloud">
                            {caseItem.contact.firstName} {caseItem.contact.lastName}
                          </p>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={cn('badge inline-flex items-center gap-1', priorityBadge.className)}>
                        <PriorityIcon className="w-3 h-3" />
                        {priorityBadge.label}
                      </span>
                    </td>
                    <td>
                      <span className={cn('badge', statusBadge.className)}>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td>
                      {caseItem.assignedToName ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                            <User className="w-3 h-3 text-pond dark:text-pond-light" />
                          </div>
                          <span className="text-sm text-slate dark:text-mist">{caseItem.assignedToName}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-chalkboard dark:text-rain-cloud italic">Unassigned</span>
                      )}
                    </td>
                    <td className="text-sm text-chalkboard dark:text-rain-cloud">
                      {formatRelativeTime(new Date(caseItem.updatedAt))}
                    </td>
                    <td>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/cases/${caseItem.id}`)}
                          className="btn-ghost btn-icon btn-sm"
                          title="Reply"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        {!['resolved', 'closed'].includes(caseItem.status) && canWrite('cases') && (
                          <button
                            onClick={(e) => handleQuickResolve(caseItem, e)}
                            className="btn-ghost btn-icon btn-sm text-turquoise dark:text-aqua"
                            title="Resolve"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && cases.length === 0 && (
          <div className="p-8 text-center">
            <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50 text-chalkboard dark:text-rain-cloud" />
            <p className="text-chalkboard dark:text-rain-cloud">No cases found.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            Page {currentPage} of {totalPages} • {pageInfo?.totalCount || 0} total cases
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn-secondary btn-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="btn-secondary btn-sm"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create Case Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="New Case"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Client <span className="text-rust">*</span>
            </label>
            <div className="relative" ref={clientSearchRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray pointer-events-none" />
                <input
                  type="text"
                  value={clientSearchQuery}
                  onChange={(e) => {
                    setClientSearchQuery(e.target.value);
                    setShowClientDropdown(true);
                    if (!e.target.value) {
                      setFormOrganizationId('');
                    }
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  placeholder="Search for a client..."
                  className="input"
                  style={{ paddingLeft: '2.5rem', paddingRight: '4rem' }}
                  disabled={isSaving}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {formOrganizationId && (
                    <button
                      type="button"
                      onClick={handleClearClient}
                      className="p-1 hover:bg-pale-gray-2 dark:hover:bg-night-pond rounded"
                      disabled={isSaving}
                    >
                      <X className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                    </button>
                  )}
                  <ChevronDown className={cn(
                    "w-4 h-4 text-chalkboard dark:text-rain-cloud transition-transform",
                    showClientDropdown && "rotate-180"
                  )} />
                </div>
              </div>
              
              {/* Client Dropdown */}
              {showClientDropdown && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white dark:bg-navy border border-gray dark:border-night-pond rounded-lg shadow-lg max-h-60 overflow-auto">
                  {orgsLoading ? (
                    <div className="p-3 text-center text-sm text-chalkboard dark:text-rain-cloud flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Searching...
                    </div>
                  ) : filteredOrganizations.length === 0 ? (
                    <div className="p-3 text-center text-sm text-chalkboard dark:text-rain-cloud">
                      {clientSearchQuery ? 'No clients found' : 'Type to search clients...'}
                    </div>
                  ) : (
                    filteredOrganizations.map((org) => {
                      const OrgIcon = org.type === 'PSP' ? Handshake : Building2;
                      return (
                        <button
                          key={org.id}
                          type="button"
                          onClick={() => handleSelectClient(org.id, org.name)}
                          className={cn(
                            "w-full px-3 py-2 text-left text-sm hover:bg-pale-gray-2 dark:hover:bg-night-pond transition-colors flex items-center gap-3",
                            formOrganizationId === org.id && "bg-pond/10 dark:bg-pond-light/10"
                          )}
                        >
                          <OrgIcon className={cn(
                            "w-4 h-4 flex-shrink-0",
                            org.type === 'PSP' ? "text-violet dark:text-violet-light" : "text-pond dark:text-pond-light"
                          )} />
                          <span className="text-slate dark:text-mist truncate flex-1">{org.name}</span>
                          <span className="text-xs text-chalkboard dark:text-rain-cloud flex-shrink-0">{org.type}</span>
                          {formOrganizationId === org.id && (
                            <CheckCircle2 className="w-4 h-4 text-pond dark:text-pond-light flex-shrink-0" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              
              {/* Selected client indicator */}
              {selectedOrganization && !showClientDropdown && (
                <p className="text-xs text-turquoise dark:text-aqua mt-1">
                  ✓ {selectedOrganization.name} selected
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Subject <span className="text-rust">*</span>
            </label>
            <input
              type="text"
              value={formSubject}
              onChange={(e) => setFormSubject(e.target.value)}
              placeholder="Brief description of the issue"
              className="input"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Description
            </label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Detailed description of the issue..."
              rows={4}
              className="input resize-none"
              disabled={isSaving}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Type
              </label>
              <select
                value={formCaseType}
                onChange={(e) => setFormCaseType(e.target.value as CaseType)}
                className="select"
                disabled={isSaving}
              >
                <option value="software">Software</option>
                <option value="services">Services</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Priority
              </label>
              <select
                value={formPriority}
                onChange={(e) => setFormPriority(e.target.value as CasePriority)}
                className="select"
                disabled={isSaving}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateCase}
              className="btn-primary"
              disabled={!formSubject || !formOrganizationId || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Case'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
